import React, { useState, useEffect, useRef } from 'react';
import * as maptilersdk from '@maptiler/sdk';
import '@maptiler/sdk/dist/maptiler-sdk.css';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import { TimelineShape } from './TimelineSchedule';
import {
  drawMapFeatures,
  MapState,
  createArrowImageForMap,
} from './mapUtils/timelineMapUtils';

// MapTiler API key from environment
const MAPTILER_API_KEY = import.meta.env.VITE_MAPTILER_API_KEY;

interface TimelineMapProps {
  timeline: TimelineShape;
  departureDate?: string;
  selectedDay?: number | null;
}

const TimelineMap: React.FC<TimelineMapProps> = ({ timeline, selectedDay = null }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maptilersdk.Map | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mapState, setMapState] = useState<MapState>({
    markers: [],
    lineSources: [],
    lineLayers: [],
    labelSources: [],
    labelLayers: [],
    isPersistentElementsDrawn: false,
  });

  // Initialize intersection observer to detect when map is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once the map is initialized, we can disconnect the observer
          observer.disconnect();
        }
      },
      {
        threshold: 0.1, // Trigger when at least 10% of the element is visible
        rootMargin: '50px', // Start loading slightly before the element comes into view
      },
    );

    if (mapContainer.current) {
      observer.observe(mapContainer.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Initialize the map when the component becomes visible
  useEffect(() => {
    if (isVisible && !map.current) {
      initializeMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // Update map when selected day changes
  useEffect(() => {
    if (map.current) {
      // Don't clear persistent elements, just redraw with new selection
      drawMapFeatures(map, timeline, selectedDay, setMapState, mapState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, timeline]);

  const initializeMap = () => {
    if (map.current) {
      return;
    } // Initialize only once

    maptilersdk.config.apiKey = MAPTILER_API_KEY;

    // Create map
    map.current = new maptilersdk.Map({
      container: mapContainer.current!,
      style: `https://api.maptiler.com/maps/basic-v2/style.json?key=${MAPTILER_API_KEY}`,
      zoom: 1,
      cooperativeGestures: false,
      attributionControl: false,
      navigationControl: true, // Enable navigation controls
      scrollZoom: true, // Enable scroll zooming
      touchZoomRotate: true, // Enable pinch zooming
    });

    // Set custom properties for the map
    map.current.getCanvas().style.cursor = 'default';

    // Create arrow icon for line decorations
    createArrowImageForMap(map);

    // Draw markers when map loads
    map.current.on('load', () => {
      drawMapFeatures(map, timeline, selectedDay, setMapState, mapState);
    });
  };

  return <MapContainer ref={mapContainer} />;
};

export { TimelineMap };

const MapContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  borderRadius: '0',
  overflow: 'hidden',
  '& .maplibregl-ctrl-bottom-right': {
    opacity: 0.1,
  },
  // Show navigation controls
  '& .maplibregl-ctrl-top-right': {
    display: 'block',
  },
}));
